import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUserRole } from '../../context/UserRoleContext';
import {
  resolveUserRolesByEmailOrUid,
  isPrivileged,
  normalizeRoleName,
} from '../../utils/roleResolver';
import {
  fetchCategories,
  uploadDocument,
  uploadLink,
  listDocuments,
  listAccessibleDocuments,
  deleteDocument,
  updateDocumentMetadata,
  reuploadDocument,
  initializeCategories,
  logDocumentBankView,
} from '../../services/documentBankService';
import {
  HiFolder,
  HiDocumentText,
  HiCloudArrowUp,
  HiMagnifyingGlass,
  HiArrowDownTray,
  HiTrash,
  HiPencilSquare,
  HiArrowPath,
  HiLink,
  HiChevronDown,
  HiFunnel,
  HiSquares2X2,
  HiListBullet,
  HiEye,
  HiDocumentDuplicate,
  HiClock,
  HiUser,
  HiGlobeAlt,
  HiInformationCircle,
} from 'react-icons/hi2';
import { motion } from 'framer-motion';
import { HiX } from 'react-icons/hi';
import { listAllPeople } from '../../services/userDirectoryService';
import Toaster from '../ui/Toaster';
import UploadPanel from './UploadPanel';
import UnifiedSearchPanel from './UnifiedSearchPanel';
import DocumentsHeader from './DocumentsHeader';
import DocumentsGrid from './DocumentsGrid';
import DocumentsList from './DocumentsList';
import { toast } from '../../utils/toast';
import PreviewModal from './PreviewModal';
import DetailsModal from './DetailsModal';
import EmptyState from './EmptyState';
import DocumentsLoading from './DocumentsLoading';
import ShareModal from './ShareModal';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import RenameModal from './RenameModal';
import ReuploadModal from './ReuploadModal';

const ACCEPTED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'image/png',
  'image/jpeg',
  'image/gif',
];

export default function DocumentBankPage({
  forClientId = null,
  forClientEmail = null,
  allowStaffOnly = true,
  allowGlobalScope = true,
  defaultCategory = null,
}) {
  const { currentUser } = useAuth();
  const { userRole } = useUserRole();

  const isClient = userRole === 'client' || userRole === 'entrepreneur';
  const isCoach = userRole === 'coach' || userRole === 'businessCoach';
  const isAdmin =
    userRole === 'admin' ||
    userRole === 'admins' ||
    userRole === 'superAdmin' ||
    userRole === 'superAdmins';

  // (moved isPrivilegedUser below to avoid referencing resolvedRoles before declaration)

  // Library owner: explicit client context for staff; clients always see their own vault.
  const libraryOwnerId = forClientId ?? (isClient ? currentUser?.uid : null);
  const ownerId = libraryOwnerId ?? currentUser?.uid;
  const ownerEmail = forClientEmail || currentUser?.email;

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(defaultCategory || '');
  const [search, setSearch] = useState('');
  const [fileTypeFilter, setFileTypeFilter] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [notes, setNotes] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkName, setLinkName] = useState('');
  const [uploadType, setUploadType] = useState('file'); // 'file' or 'link'
  const [docs, setDocs] = useState([]);
  const [showStaffOnly, setShowStaffOnly] = useState(false);
  const [selectedScope, setSelectedScope] = useState('client');
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, type: null, doc: null });
  const [renameValue, setRenameValue] = useState('');
  const [reuploadFile, setReuploadFile] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [resolvedRoles, setResolvedRoles] = useState({
    roles: ['Client'],
    highestRole: 'Client',
    matchedInClients: false,
    documentBankStaffFullAccess: false,
  });
  const [activeTab, setActiveTab] = useState('all');
  const [people, setPeople] = useState([]);
  const [visibilityType, setVisibilityType] = useState('private');
  const [visibilityRoles, setVisibilityRoles] = useState([]);
  const [visibilityUsers, setVisibilityUsers] = useState([]);
  const [previewModal, setPreviewModal] = useState({ open: false, doc: null });
  const [detailsModal, setDetailsModal] = useState({ open: false, doc: null });
  const viewLogCache = useRef(new Set());

  const canUpload = !!currentUser;

  const isPrivilegedUser =
    isAdmin ||
    userRole === 'superAdmin' ||
    userRole === 'superAdmins' ||
    userRole === 'PlatformEngineer' ||
    (Array.isArray(resolvedRoles.roles) &&
      resolvedRoles.roles.some(r =>
        [
          'Admin',
          'Moderator',
          'Coach',
          'BusinessAdvisor',
          'Business Advisor',
          'superAdmin',
          'SuperAdmins',
          'SuperAdmin',
          'PlatformEngineer',
        ].includes(r)
      ));

  const canManageDoc = doc => {
    if (!currentUser?.uid || !doc) return false;
    if (resolvedRoles.documentBankStaffFullAccess === true) return true;
    if (
      isAdmin ||
      isCoach ||
      userRole === 'moderator' ||
      userRole === 'superAdmin' ||
      userRole === 'superAdmins' ||
      userRole === 'PlatformEngineer'
    )
      return true;
    return doc.ownerId === currentUser.uid;
  };

  useEffect(() => {
    let mounted = true;
    fetchCategories().then(list => mounted && setCategories(list));
    return () => (mounted = false);
  }, []);

  // Set default category when it changes
  useEffect(() => {
    if (defaultCategory) {
      setSelectedCategory(defaultCategory);
    }
  }, [defaultCategory]);

  // Initialize categories in Firestore for privileged users (admin, coach, business advisor)
  useEffect(() => {
    let mounted = true;
    if (isPrivilegedUser) {
      (async () => {
        try {
          await initializeCategories();
        } catch (error) {
          toast.error('Failed to initialize categories');
        }
      })();
    }
    return () => (mounted = false);
  }, [isPrivilegedUser]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!currentUser?.email && !currentUser?.uid) return;
      const result = await resolveUserRolesByEmailOrUid({
        email: currentUser?.email || null,
        uid: currentUser?.uid || null,
      });
      if (mounted) setResolvedRoles(result);
    })();
    return () => {
      mounted = false;
    };
  }, [currentUser?.email, currentUser?.uid]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const list = await listAllPeople({ includeClients: true });
        if (mounted) setPeople(list);
      } catch (_) {}
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const refreshList = useCallback(async () => {
    setLoading(true);
    try {
      const staffFullVaultAccess = resolvedRoles.documentBankStaffFullAccess === true || isAdmin;

      const stripInternalSnap = rows =>
        (rows || []).map(d => {
          const { _snap, ...rest } = d;
          return rest;
        });

      if (staffFullVaultAccess && ownerId) {
        const { items } = await listDocuments({
          forOwnerId: ownerId,
          category: selectedCategory || null,
          fileType: fileTypeFilter || null,
          search,
          pageSize: 60,
          last: null,
          includeStaffOnly: !!(allowStaffOnly && showStaffOnly),
          scope: allowGlobalScope ? null : selectedScope || 'client',
        });
        setDocs(stripInternalSnap(items));
      } else {
        const viewer = {
          uid: currentUser?.uid || null,
          email: currentUser?.email || null,
          roles: resolvedRoles.roles,
          highestRole: resolvedRoles.highestRole,
          matchedInClients: resolvedRoles.matchedInClients,
          isAdmin: isAdmin,
          documentBankStaffFullAccess: resolvedRoles.documentBankStaffFullAccess === true,
        };
        let { items } = await listAccessibleDocuments({
          viewer,
          ownerId: libraryOwnerId,
          category: selectedCategory || null,
          fileType: fileTypeFilter || null,
          search,
          pageSize: 60,
        });
        items = items || [];
        const scopeKey = allowGlobalScope ? null : selectedScope || 'client';
        if (scopeKey) {
          items = items.filter(d => !d.scope || d.scope === scopeKey);
        }
        if (allowStaffOnly && showStaffOnly) {
          const staffRoles = new Set([
            'admin',
            'admins',
            'superadmin',
            'superadmins',
            'moderator',
            'moderators',
            'coach',
            'coaches',
            'businesscoach',
            'platformengineer',
            'developer',
            's_admin',
          ]);
          items = items.filter(d => staffRoles.has(String(d.uploadedByRole || '').toLowerCase()));
        }
        setDocs(items);
      }
    } catch (err) {
      const code = err?.code || '';
      if (code === 'permission-denied') {
        toast.error('Could not load documents: permission denied.');
      } else {
        toast.error(err?.message || 'Failed to load documents');
      }
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [
    currentUser?.uid,
    currentUser?.email,
    resolvedRoles.roles,
    resolvedRoles.highestRole,
    resolvedRoles.matchedInClients,
    resolvedRoles.documentBankStaffFullAccess,
    isAdmin,
    ownerId,
    libraryOwnerId,
    selectedCategory,
    fileTypeFilter,
    search,
    selectedScope,
    allowGlobalScope,
    allowStaffOnly,
    showStaffOnly,
  ]);

  const documentBankListDepsKey = useMemo(
    () =>
      [
        currentUser?.uid ?? '',
        selectedCategory ?? '',
        fileTypeFilter ?? '',
        search ?? '',
        selectedScope ?? '',
        resolvedRoles.highestRole ?? '',
        (resolvedRoles.roles ?? []).join(','),
        resolvedRoles.documentBankStaffFullAccess ? '1' : '0',
        forClientId ?? '',
        isClient ? '1' : '0',
        isAdmin ? '1' : '0',
        allowGlobalScope ? '1' : '0',
        allowStaffOnly && showStaffOnly ? '1' : '0',
      ].join('|'),
    [
      currentUser?.uid,
      selectedCategory,
      fileTypeFilter,
      search,
      selectedScope,
      resolvedRoles.highestRole,
      resolvedRoles.roles,
      resolvedRoles.documentBankStaffFullAccess,
      forClientId,
      isClient,
      isAdmin,
      allowGlobalScope,
      allowStaffOnly,
      showStaffOnly,
    ]
  );

  useEffect(() => {
    if (!currentUser?.uid) return;
    refreshList();
  }, [documentBankListDepsKey, currentUser?.uid, refreshList]);

  useEffect(() => {
    if (!currentUser?.uid) return;
    if (!forClientId) return;
    if (forClientId === currentUser.uid) return;
    if (!isPrivilegedUser) return;

    const cacheKey = `${currentUser.uid}:${forClientId}`;
    if (viewLogCache.current.has(cacheKey)) return;

    const viewerName = currentUser.displayName || currentUser.email || 'Team member';

    logDocumentBankView({
      clientId: forClientId,
      clientEmail: forClientEmail || '',
      viewerId: currentUser.uid,
      viewerEmail: currentUser.email || '',
      viewerName,
      viewerRole: userRole,
    }).finally(() => {
      viewLogCache.current.add(cacheKey);
    });
  }, [
    currentUser?.uid,
    currentUser?.email,
    currentUser?.displayName,
    forClientId,
    forClientEmail,
    isPrivilegedUser,
    userRole,
  ]);

  const handleUpload = async ({
    uploadType: type = uploadType,
    linkUrl: url = linkUrl,
    linkName: name = linkName,
  } = {}) => {
    const finalUploadType = type || uploadType;
    const finalLinkUrl = url || linkUrl;
    const finalLinkName = name || linkName;

    if (!selectedCategory) {
      toast.error('Please choose a category');
      return;
    }

    if (finalUploadType === 'file') {
      if (!selectedFile) {
        toast.error('Please select a file');
        return;
      }
      if (!ACCEPTED_TYPES.includes(selectedFile.type)) {
        toast.error('Unsupported file type');
        return;
      }
    } else if (finalUploadType === 'link') {
      if (!finalLinkUrl || !finalLinkUrl.trim()) {
        toast.error('Please enter a link URL');
        return;
      }
    }

    try {
      setUploading(true);
      const roleSet = new Set((visibilityRoles || []).map(r => normalizeRoleName(r)));
      const userIds = (visibilityUsers || []).map(u => u.uid).filter(Boolean);
      const userEmails = (visibilityUsers || []).map(u => u.email).filter(Boolean);
      const visibility = {
        type: visibilityType,
        roles: Array.from(roleSet),
        userIds,
        userEmails,
      };

      if (finalUploadType === 'file') {
        await uploadDocument({
          file: selectedFile,
          ownerId,
          ownerEmail,
          category: selectedCategory,
          uploadedByRole: userRole,
          notes,
          visibility,
          scope: selectedScope,
        });
        setSelectedFile(null);
        toast.success('Document uploaded');
      } else {
        await uploadLink({
          link: finalLinkUrl.trim(),
          linkName: finalLinkName.trim() || undefined,
          ownerId,
          ownerEmail,
          category: selectedCategory,
          uploadedByRole: userRole,
          notes,
          visibility,
          scope: selectedScope,
        });
        setLinkUrl('');
        setLinkName('');
        toast.success('Link added successfully');
      }

      setNotes('');
      await refreshList();
    } catch (err) {
      const errorMessage = err?.message || 'Upload failed';
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    const item = modal.doc;
    if (!item) return;
    try {
      await deleteDocument({ id: item.id, storagePath: item.storagePath });
      setModal({ open: false, type: null, doc: null });
      await refreshList();
      toast.success('Document deleted');
    } catch (e) {
      setModal({ open: false, type: null, doc: null });
      toast.error('Failed to delete');
    }
  };

  const openRename = item => {
    if (!canManageDoc(item)) return;
    setRenameValue(item.fileName || '');
    setModal({ open: true, type: 'rename', doc: item });
  };
  const handleRenameConfirm = async () => {
    const item = modal.doc;
    if (!item) return;
    const newName = (renameValue || '').trim();
    if (!newName || newName === item.fileName) {
      setModal({ open: false, type: null, doc: null });
      return;
    }
    try {
      await updateDocumentMetadata(item.id, { fileName: newName });
      setModal({ open: false, type: null, doc: null });
      await refreshList();
      toast.success('Document renamed');
    } catch (e) {
      setModal({ open: false, type: null, doc: null });
      toast.error('Rename failed');
    }
  };

  const openReupload = item => {
    if (!canManageDoc(item)) return;
    setReuploadFile(null);
    setModal({ open: true, type: 'reupload', doc: item });
  };
  const handleDeleteClick = item => {
    if (!canManageDoc(item)) return;
    setModal({ open: true, type: 'delete', doc: item });
  };
  const handleReuploadConfirm = async () => {
    const item = modal.doc;
    if (!item || !reuploadFile) {
      setModal({ open: false, type: null, doc: null });
      return;
    }
    try {
      await reuploadDocument({
        id: item.id,
        oldStoragePath: item.storagePath,
        newFile: reuploadFile,
        ownerId: item.ownerId,
        category: item.category,
        uploadedByRole: userRole,
      });
      setModal({ open: false, type: null, doc: null });
      await refreshList();
      toast.success('Document replaced');
    } catch (e) {
      setModal({ open: false, type: null, doc: null });
      toast.error('Re-upload failed');
    }
  };

  const openShare = item => {
    setModal({ open: true, type: 'share', doc: item });
  };
  const handleCopyLink = () => {
    if (modal.doc?.url) navigator.clipboard.writeText(modal.doc.url);
    setModal({ open: false, type: null, doc: null });
    toast.success('Link copied to clipboard');
  };

  const fileTypeOptions = useMemo(
    () => [
      { label: 'All types', value: '' },
      { label: 'PDF', value: 'application/pdf' },
      {
        label: 'Word',
        value: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      },
      {
        label: 'Excel',
        value: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
      { label: 'Image (PNG)', value: 'image/png' },
      { label: 'Image (JPG)', value: 'image/jpeg' },
    ],
    []
  );

  const isPreviewable = type =>
    type?.startsWith('image/') ||
    type === 'application/pdf' ||
    type === 'application/msword' ||
    type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    type === 'application/vnd.ms-excel' ||
    type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

  const openPreview = doc => {
    setPreviewModal({ open: true, doc });
  };

  const openDetails = doc => {
    setDetailsModal({ open: true, doc });
  };

  const sortedDocs = useMemo(() => {
    if (!docs.length) return [];

    return [...docs].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'name':
          comparison = (a.fileName || '').localeCompare(b.fileName || '');
          break;
        case 'size':
          comparison = (a.fileSize || 0) - (b.fileSize || 0);
          break;
        case 'type':
          comparison = (a.fileType || '').localeCompare(b.fileType || '');
          break;
        case 'date':
        default:
          {
            const aMs = a.uploadedAt?.toMillis
              ? a.uploadedAt.toMillis()
              : typeof a.uploadedAt === 'number'
                ? a.uploadedAt
                : new Date(a.uploadedAt || 0).getTime();
            const bMs = b.uploadedAt?.toMillis
              ? b.uploadedAt.toMillis()
              : typeof b.uploadedAt === 'number'
                ? b.uploadedAt
                : new Date(b.uploadedAt || 0).getTime();
            comparison = bMs - aMs;
          }
          break;
      }

      return sortOrder === 'desc' ? -comparison : comparison;
    });
  }, [docs, sortBy, sortOrder]);

  const filteredByTab = useMemo(() => {
    if (!sortedDocs.length) return [];
    const uid = currentUser?.uid;
    switch (activeTab) {
      case 'mine':
        return sortedDocs.filter(d => d.ownerId === uid);
      case 'shared':
        return sortedDocs.filter(
          d => Array.isArray(d?.visibility?.userIds) && d.visibility.userIds.includes(uid)
        );
      case 'public':
        return sortedDocs.filter(d => d?.visibility?.type === 'public');
      case 'byRole':
        return sortedDocs.filter(d =>
          (d?.visibility?.roles || []).some(r => (resolvedRoles.roles || []).includes(r))
        );
      case 'byUser':
        return sortedDocs.filter(
          d => Array.isArray(d?.visibility?.userIds) && d.visibility.userIds.includes(uid)
        );
      case 'all':
      default:
        return sortedDocs;
    }
  }, [sortedDocs, activeTab, currentUser?.uid, resolvedRoles.roles]);

  const formatFileSize = bytes => {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = value => {
    if (!value) return 'Unknown';
    const ms = value?.toMillis
      ? value.toMillis()
      : typeof value === 'number'
        ? value
        : new Date(value).getTime();
    if (!ms || Number.isNaN(ms)) return 'Unknown';
    return new Date(ms).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Removed intrusive toast on every docs update - was too noisy

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-50">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-8 flex flex-col gap-6 sm:mb-12 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="flex items-center gap-5">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
                className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 via-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/25 ring-4 ring-blue-100"
              >
                <HiDocumentText className="h-7 w-7" />
              </motion.div>
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                  Document Bank
                </h1>
                <p className="mt-2 text-base text-gray-600 sm:text-lg">
                  {isClient
                    ? 'View and manage your uploaded documents'
                    : isPrivilegedUser
                      ? 'Manage and oversee all client documents with full control'
                      : 'Centralized document repository and management system'}
                </p>
              </div>
            </div>

            {/* Actions */}
            {canUpload && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="flex flex-wrap items-center gap-3 sm:gap-3"
              >
                <button
                  onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                  className="group inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition-all hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 hover:shadow-md active:scale-[0.97]"
                >
                  {viewMode === 'grid' ? (
                    <HiListBullet className="h-4 w-4 transition-transform group-hover:scale-110" />
                  ) : (
                    <HiSquares2X2 className="h-4 w-4 transition-transform group-hover:scale-110" />
                  )}
                  <span className="hidden sm:inline">
                    {viewMode === 'grid' ? 'List View' : 'Grid View'}
                  </span>
                </button>
              </motion.div>
            )}
          </motion.div>

          {/* Unified Search & Filters Panel */}
          <UnifiedSearchPanel
            isClient={isClient}
            search={search}
            setSearch={setSearch}
            categories={categories}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            fileTypeFilter={fileTypeFilter}
            setFileTypeFilter={setFileTypeFilter}
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            fileTypeOptions={fileTypeOptions}
            showFilters={true}
          />

          {/* Content - Vertical Stack */}
          <div className="mt-10 space-y-8">
            {/* Upload Panel */}
            {canUpload && (
              <div>
                <UploadPanel
                  canUpload={canUpload}
                  categories={categories}
                  selectedCategory={selectedCategory}
                  setSelectedCategory={setSelectedCategory}
                  selectedFile={selectedFile}
                  setSelectedFile={setSelectedFile}
                  notes={notes}
                  setNotes={setNotes}
                  uploading={uploading}
                  onUpload={handleUpload}
                  ACCEPTED_TYPES={ACCEPTED_TYPES}
                  visibilityType={visibilityType}
                  setVisibilityType={setVisibilityType}
                  visibilityRoles={visibilityRoles}
                  setVisibilityRoles={setVisibilityRoles}
                  people={people}
                  visibilityUsers={visibilityUsers}
                  setVisibilityUsers={setVisibilityUsers}
                  formatFileSize={formatFileSize}
                  linkUrl={linkUrl}
                  setLinkUrl={setLinkUrl}
                  linkName={linkName}
                  setLinkName={setLinkName}
                  uploadType={uploadType}
                  setUploadType={setUploadType}
                />
              </div>
            )}

            {/* Documents Display */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg shadow-gray-200/50 ring-1 ring-gray-100"
              >
                <DocumentsHeader
                  count={filteredByTab.length}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  isClient={isClient}
                />
                <div className="p-4 sm:p-6 lg:p-8">
                  {loading ? (
                    <DocumentsLoading viewMode={viewMode} />
                  ) : filteredByTab.length === 0 ? (
                    <EmptyState canUpload={canUpload} />
                  ) : viewMode === 'grid' ? (
                    <DocumentsGrid
                      docs={filteredByTab}
                      isPreviewable={isPreviewable}
                      canManageDoc={canManageDoc}
                      openDetails={openDetails}
                      openPreview={openPreview}
                      openShare={openShare}
                      openRename={openRename}
                      openReupload={openReupload}
                      onDelete={handleDeleteClick}
                      formatFileSize={formatFileSize}
                      formatDate={formatDate}
                    />
                  ) : (
                    <DocumentsList
                      docs={filteredByTab}
                      isPreviewable={isPreviewable}
                      canManageDoc={canManageDoc}
                      openDetails={openDetails}
                      openPreview={openPreview}
                      openShare={openShare}
                      openRename={openRename}
                      openReupload={openReupload}
                      onDelete={handleDeleteClick}
                      formatFileSize={formatFileSize}
                      formatDate={formatDate}
                    />
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      <PreviewModal
        open={previewModal.open}
        doc={previewModal.doc}
        onClose={() => setPreviewModal({ open: false, doc: null })}
        formatFileSize={formatFileSize}
      />

      <DetailsModal
        open={detailsModal.open}
        doc={detailsModal.doc}
        onClose={() => setDetailsModal({ open: false, doc: null })}
        isPreviewable={isPreviewable}
        openPreview={openPreview}
        formatFileSize={formatFileSize}
        formatDate={formatDate}
      />

      <ShareModal
        open={modal.open && modal.type === 'share'}
        doc={modal.doc}
        onClose={() => setModal({ open: false, type: null, doc: null })}
        onCopy={handleCopyLink}
      />
      <ConfirmDeleteModal
        open={modal.open && modal.type === 'delete'}
        doc={modal.doc}
        onCancel={() => setModal({ open: false, type: null, doc: null })}
        onConfirm={handleDeleteConfirm}
      />
      <RenameModal
        open={modal.open && modal.type === 'rename'}
        value={renameValue}
        onChange={setRenameValue}
        onCancel={() => setModal({ open: false, type: null, doc: null })}
        onConfirm={handleRenameConfirm}
      />
      <ReuploadModal
        open={modal.open && modal.type === 'reupload'}
        accept={ACCEPTED_TYPES.join(',')}
        file={reuploadFile}
        onChange={setReuploadFile}
        onCancel={() => setModal({ open: false, type: null, doc: null })}
        onConfirm={handleReuploadConfirm}
        formatFileSize={formatFileSize}
      />

      <Toaster />
    </>
  );
}
